import React, { useState, useRef } from "react";
import Header from "../components/layout/Header";
import Footer from "./Footer";

const BRAND   = "#1F4E8C";
const COMPANY = "Suvidha Elder Care";
const DOMAIN  = "suvidhaeldercare.com";
const SUPPORT_EMAIL = "support@suvidhaeldercare.com";
const DEV_CO  = "Mindhuntz (mindhuntz.com)";

/* Placeholders — client will supply real values later */
const LEGAL_ENTITY        = "[Full Legal Entity Name]";
const REGISTERED_ADDRESS  = "[Registered Address]";
const COMPLETE_ADDRESS    = "[Complete Registered Address]";
const SUPPORT_PHONE       = "[Official Customer Support Number]";
const GRIEVANCE_OFFICER   = "[Name and Designation]";
const GRIEVANCE_EMAIL     = "[Grievance Email Address]";
const EFFECTIVE_DATE      = "[DD Month YYYY]";
const LAST_UPDATED        = "[DD Month YYYY]";
const JURISDICTION        = "Hyderabad, Telangana";

const TABS = [
  { id: "terms",   label: "Terms & Conditions" },
  { id: "refund",  label: "Refund Policy" },
  { id: "privacy", label: "Privacy Policy" },
  { id: "cookies", label: "Cookies Policy" },
  { id: "disclaimer", label: "Disclaimer" },
];

/* ── Reusable section heading ───────────────────────── */
function SectionH({ n, children }) {
  return (
    <h3 style={{
      fontSize: 16, fontWeight: 800, color: BRAND,
      margin: "28px 0 8px", display: "flex", gap: 8, alignItems: "baseline"
    }}>
      <span style={{
        background: BRAND, color: "#fff",
        fontSize: 11, fontWeight: 900,
        padding: "2px 8px", borderRadius: 20, flexShrink: 0
      }}>{n}</span>
      {children}
    </h3>
  );
}

function SubHead({ children }) {
  return (
    <p style={{ fontSize: 14, fontWeight: 800, color: "#1E293B", margin: "16px 0 6px" }}>
      {children}
    </p>
  );
}

function Para({ children }) {
  return <p style={{ fontSize: 14, color: "#374151", lineHeight: 1.85, margin: "0 0 14px" }}>{children}</p>;
}

function BulletList({ items }) {
  return (
    <ul style={{ paddingLeft: 0, margin: "0 0 14px", listStyle: "none" }}>
      {items.map((item, i) => (
        <li key={i} style={{
          fontSize: 14, color: "#374151", lineHeight: 1.75,
          marginBottom: 8, display: "flex", gap: 10, alignItems: "flex-start"
        }}>
          <span style={{ color: BRAND, fontWeight: 900, flexShrink: 0, marginTop: 2 }}>✓</span>
          <span>{item}</span>
        </li>
      ))}
    </ul>
  );
}

function EffectiveDates() {
  return (
    <div style={{
      display: "flex", gap: 16, flexWrap: "wrap",
      fontSize: 13, color: "#64748B", marginBottom: 18
    }}>
      <span><strong style={{ color: "#1E293B" }}>Effective Date:</strong> {EFFECTIVE_DATE}</span>
      <span><strong style={{ color: "#1E293B" }}>Last Updated:</strong> {LAST_UPDATED}</span>
    </div>
  );
}

function GrievanceBlock({ title }) {
  return (
    <div style={{
      background: "#F8FAFC", border: "1px solid #E2E8F0",
      borderRadius: 14, padding: "16px 20px", marginTop: 8
    }}>
      <div style={{ fontWeight: 800, color: "#1E293B", marginBottom: 8, fontSize: 14 }}>
        {title}
      </div>
      <p style={{ margin: 0, fontSize: 13, color: "#374151", lineHeight: 1.9 }}>
        <strong>{COMPANY}</strong><br />
        Legal Entity: {LEGAL_ENTITY}<br />
        Registered Address: {COMPLETE_ADDRESS}<br />
        Email: <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: BRAND, fontWeight: 700 }}>{SUPPORT_EMAIL}</a><br />
        Customer-Support Number: {SUPPORT_PHONE}<br />
        Grievance Officer: {GRIEVANCE_OFFICER}<br />
        Grievance Email: {GRIEVANCE_EMAIL}
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   TAB CONTENT COMPONENTS
═══════════════════════════════════════════════════════ */

function TermsContent() {
  return (
    <div>
      <EffectiveDates />
      <Para>
        Welcome to {COMPANY}. These Terms and Conditions govern your access to and use of the Suvidha Elder Care website ({DOMAIN}), mobile application, care plans, bookings and related services.
      </Para>
      <Para>
        By registering, purchasing a plan, booking a service or otherwise using the platform, you confirm that you have read, understood and agreed to these Terms and Conditions.
      </Para>

      <SectionH n="1">About Suvidha Elder Care</SectionH>
      <Para>
        {COMPANY} is operated by <strong>{LEGAL_ENTITY}</strong>, having its registered office at <strong>{REGISTERED_ADDRESS}</strong>, hereinafter referred to as "Suvidha," "we," "us" or "our."
      </Para>
      <Para>
        Suvidha facilitates and coordinates elder-care services, including home healthcare, doctor consultations, nursing, physiotherapy, laboratory and diagnostic services, medical equipment, hospital assistance, care coordination and other support services displayed on the platform.
      </Para>
      <Para>
        Depending on the service selected, services may be delivered directly by Suvidha or through independent healthcare professionals, diagnostic centres, hospitals, pharmacies, equipment suppliers, caregivers or other authorised service partners.
      </Para>

      <SectionH n="2">Eligibility</SectionH>
      <Para>
        You must be at least 18 years old and legally capable of entering into a contract to create an account, purchase a plan or book a service.
      </Para>
      <Para>If you register or make a booking for a parent, spouse, relative or another individual, you confirm that:</Para>
      <BulletList items={[
        "You are authorised to act on their behalf.",
        "You have provided accurate information.",
        "The care recipient has consented to the service where consent is legally and practically required.",
        "You will communicate all relevant service information to the care recipient.",
      ]} />

      <SectionH n="3">Nature of the Services</SectionH>
      <Para>
        Suvidha provides elder-care coordination and access to services based on availability, location, selected plan and the care recipient's requirements.
      </Para>
      <Para>Services may include:</Para>
      <BulletList items={[
        "Doctor consultations and home visits",
        "Nursing and attendant support",
        "Physiotherapy and rehabilitation",
        "Health monitoring",
        "Laboratory and diagnostic services",
        "Hospital admission and discharge assistance",
        "Ambulance and transport coordination",
        "Medication support and reminders",
        "Medical equipment rental or purchase",
        "Teleconsultations",
        "Care coordination and family updates",
        "Other services displayed on the website or application",
      ]} />
      <Para>
        Service descriptions, plan benefits, frequency, duration, exclusions and prices will be communicated on the relevant service or plan page.
      </Para>

      <SectionH n="4">Medical Disclaimer</SectionH>
      <Para>
        Information available on the Suvidha platform is provided for general awareness and service coordination. It is not a substitute for an in-person medical examination, diagnosis, prescription or treatment by a qualified healthcare professional.
      </Para>
      <Para>
        Only appropriately qualified professionals may provide clinical advice, diagnose medical conditions, prescribe medicines or modify treatment.
      </Para>
      <Para>
        Users must not delay or discontinue medical treatment based solely on information available through the website or application.
      </Para>
      <Para>
        Suvidha does not guarantee any particular medical outcome, recovery period or treatment result. Healthcare outcomes can vary depending on the person's condition, medical history, response to treatment and other factors.
      </Para>

      <SectionH n="5">Emergency Situations</SectionH>
      <Para>
        The Suvidha platform is not a substitute for government emergency services, a hospital emergency department or immediate emergency medical intervention.
      </Para>
      <Para>
        If a person is experiencing chest pain, breathing difficulty, loss of consciousness, uncontrolled bleeding, stroke symptoms, a serious fall or another life-threatening condition, contact the appropriate emergency service or visit the nearest hospital immediately.
      </Para>
      <Para>
        Any emergency coordination offered by Suvidha depends on location, availability, response time, road conditions, hospital acceptance and third-party service providers. Suvidha cannot guarantee ambulance arrival times or hospital admission.
      </Para>

      <SectionH n="6">Account Registration and Security</SectionH>
      <Para>Users may be required to create an account using their name, telephone number, email address or other information.</Para>
      <Para>You are responsible for:</Para>
      <BulletList items={[
        "Providing complete and accurate information.",
        "Keeping your login credentials and OTPs confidential.",
        "Restricting unauthorised access to your account.",
        "Informing Suvidha promptly if you suspect unauthorised access.",
        "Ensuring that the contact information registered with Suvidha remains current.",
      ]} />
      <Para>
        You are responsible for activities performed through your account unless the activity resulted from a security failure directly attributable to Suvidha.
      </Para>

      <SectionH n="7">Health Information and Medical Documents</SectionH>
      <Para>
        Users may be permitted to submit prescriptions, laboratory reports, discharge summaries, medical histories, medication information and other healthcare documents.
      </Para>
      <Para>By submitting such information, you confirm that:</Para>
      <BulletList items={[
        "The information is accurate to the best of your knowledge.",
        "You are authorised to share it.",
        "You consent to its use for care coordination and service delivery.",
        "You understand that incomplete or inaccurate information may affect the service provided.",
      ]} />
      <Para>
        Uploaded documents do not automatically constitute a diagnosis, prescription or treatment plan. Medication changes and clinical decisions must be approved by a qualified healthcare professional.
      </Para>
      <Para>Medical information will be handled in accordance with the Privacy Policy and applicable law.</Para>

      <SectionH n="8">Bookings and Service Availability</SectionH>
      <Para>
        A booking request is not confirmed until the user receives confirmation through the application, website, telephone, email, SMS or WhatsApp.
      </Para>
      <Para>Bookings are subject to:</Para>
      <BulletList items={[
        "Availability of professionals and service partners",
        "Serviceable location",
        "Selected date and time",
        "Payment confirmation",
        "Medical suitability, where relevant",
        "Verification of necessary information or documents",
      ]} />
      <Para>
        Suvidha may reschedule or cancel a booking because of professional unavailability, emergencies, weather, transportation problems, technical issues or circumstances beyond reasonable control. Where possible, an alternative time or professional will be offered.
      </Para>

      <SectionH n="9">Care Plans and Subscriptions</SectionH>
      <Para>
        Care-plan benefits apply only during the active subscription period and are subject to the inclusions, usage limits and conditions displayed at the time of purchase.
      </Para>
      <Para>Unless expressly stated otherwise:</Para>
      <BulletList items={[
        "Unused services do not automatically carry forward.",
        "Plan benefits cannot be exchanged for cash.",
        "Benefits cannot be transferred to another person.",
        "Services beyond the plan allowance may be charged separately.",
        "Services remain subject to professional and geographic availability.",
        "Tests, scans, medicines, equipment, hospital expenses and third-party services are included only when expressly stated.",
      ]} />
      <Para>
        Suvidha may recommend a different service or care plan when the selected service is unsuitable for the care recipient's condition. Any change requiring additional payment will be communicated for approval.
      </Para>

      <SectionH n="10">Fees, Payments and Taxes</SectionH>
      <Para>Users must pay the amount displayed or communicated at the time of booking or subscription.</Para>
      <Para>
        Prices may include or exclude applicable taxes as stated during checkout or invoicing. Additional charges may apply for emergency requests, extended service hours, consumables, travel beyond the service area, equipment delivery, hospital expenses or services not included in the selected plan.
      </Para>
      <Para>
        Payments may be processed through third-party payment providers. Suvidha does not store complete card, banking or UPI credentials unless expressly stated and permitted by law.
      </Para>
      <Para>
        Suvidha may revise future prices and plan benefits. Changes will not ordinarily affect a confirmed and fully paid booking unless required because of a clear pricing error, change in law or mutually approved service modification.
      </Para>

      <SectionH n="11">Cancellations, Rescheduling and Refunds</SectionH>
      <Para>
        Cancellations, rescheduling requests and refunds will be governed by the Refund and Cancellation Policy displayed on the platform.
      </Para>
      <Para>Refund eligibility may depend on:</Para>
      <BulletList items={[
        "Type of service",
        "Time remaining before the appointment",
        "Whether a professional has already been assigned or dispatched",
        "Whether equipment, medicines or consumables have been arranged",
        "Charges imposed by third-party providers",
        "Whether the service has already commenced",
      ]} />
      <Para>
        Approved refunds will be processed through the original payment method within the period stated in the Refund Policy, subject to payment-provider and banking timelines.
      </Para>

      <SectionH n="12">Medical Equipment</SectionH>
      <Para>Medical equipment may be offered for sale or rental through Suvidha or an authorised supplier.</Para>
      <Para>For equipment rentals, the user must:</Para>
      <BulletList items={[
        "Use the equipment only for its intended purpose.",
        "Follow operating and safety instructions.",
        "Avoid unauthorised repair, modification or transfer.",
        "Keep the equipment reasonably safe and clean.",
        "Return it on time and in the condition in which it was supplied, subject to normal wear.",
        "Notify Suvidha promptly of damage, loss or malfunction.",
      ]} />
      <Para>
        Deposits, delivery charges, rental periods, consumables, installation, maintenance and damage charges will be communicated separately.
      </Para>
      <Para>Users must obtain professional guidance before using equipment that requires clinical supervision.</Para>

      <SectionH n="13">Third-Party Professionals and Service Providers</SectionH>
      <Para>
        Some services may be delivered by independent doctors, hospitals, laboratories, pharmacies, caregivers, ambulance providers, equipment suppliers or other third parties.
      </Para>
      <Para>
        These professionals and organisations remain responsible for the professional services they provide. Suvidha may facilitate coordination, booking, communication and payment but does not control every clinical decision or operational action of an independent provider.
      </Para>
      <Para>
        Any concern regarding a third-party service should be reported to Suvidha promptly so that it can be reviewed and appropriately escalated.
      </Para>

      <SectionH n="14">User and Family Responsibilities</SectionH>
      <Para>Users and family members must:</Para>
      <BulletList items={[
        "Provide accurate medical and personal information.",
        "Disclose known allergies, medical conditions and current medications.",
        "Maintain a safe environment for home visits.",
        "Treat professionals and support staff respectfully.",
        "Ensure that a responsible person is available when reasonably required.",
        "Follow instructions given by qualified professionals.",
        "Inform Suvidha of changes in the care recipient's condition.",
        "Pay applicable charges on time.",
        "Avoid requesting staff to perform unlawful, unsafe or unauthorised activities.",
      ]} />
      <Para>
        Suvidha may stop or refuse a service where there is abuse, harassment, unsafe working conditions, unlawful conduct, non-payment or a serious risk to the care recipient or service professional.
      </Para>

      <SectionH n="15">Communication and Family Updates</SectionH>
      <Para>
        Users consent to receiving service-related communications through telephone calls, SMS, email, WhatsApp, application notifications or other contact methods provided during registration.
      </Para>
      <Para>These communications may include:</Para>
      <BulletList items={[
        "OTPs and account notifications",
        "Appointment confirmations",
        "Service updates",
        "Payment information",
        "Care-plan reminders",
        "Health-service coordination",
        "Emergency-related communication",
      ]} />
      <Para>
        Promotional communication will be sent in accordance with the user's consent and applicable law. Users may opt out of promotional communication without affecting essential service messages.
      </Para>

      <SectionH n="16">Privacy and Data Protection</SectionH>
      <Para>
        Suvidha may collect and process personal information, health information, contact details, booking history, payment information and uploaded documents for purposes including:
      </Para>
      <BulletList items={[
        "Providing and coordinating services",
        "Managing accounts and subscriptions",
        "Communicating with users and families",
        "Maintaining care and service records",
        "Processing payments",
        "Improving platform performance",
        "Meeting legal and regulatory obligations",
        "Preventing fraud and misuse",
      ]} />
      <Para>
        Further information regarding collection, use, storage, sharing and user rights is available in the Privacy Policy.
      </Para>

      <SectionH n="17">Platform Availability</SectionH>
      <Para>
        Suvidha aims to keep the website and application accessible. However, continuous and uninterrupted availability cannot be guaranteed.
      </Para>
      <Para>
        Access may be interrupted because of maintenance, security updates, internet failure, third-party outages, system upgrades or circumstances beyond Suvidha's control.
      </Para>
      <Para>
        Suvidha may modify, improve, suspend or discontinue a platform feature where reasonably necessary. Material changes affecting paid services will be communicated where practicable.
      </Para>

      <SectionH n="18">Intellectual Property</SectionH>
      <Para>
        The Suvidha name, logo, platform design, written content, graphics, software and other materials are owned by or licensed to Suvidha.
      </Para>
      <Para>
        Users may access the platform only for personal and lawful use. No content may be copied, modified, sold, republished, reverse engineered or commercially exploited without prior written permission.
      </Para>

      <SectionH n="19">Prohibited Use</SectionH>
      <Para>Users must not:</Para>
      <BulletList items={[
        "Submit false, fraudulent or misleading information.",
        "Impersonate another person.",
        "Upload unlawful, harmful or unauthorised content.",
        "Attempt to access another user's account or health information.",
        "Interfere with platform security or operation.",
        "Use automated tools to extract platform data without permission.",
        "Misuse booking, payment, refund or communication systems.",
        "Threaten, abuse or harass professionals, caregivers or staff.",
        "Use the platform for unlawful or unauthorised purposes.",
      ]} />

      <SectionH n="20">Limitation of Liability</SectionH>
      <Para>
        To the maximum extent permitted by applicable law, Suvidha will not be liable for indirect, incidental, special or consequential losses arising from:
      </Para>
      <BulletList items={[
        "Reliance on general information available on the platform",
        "Failure to disclose complete or accurate medical information",
        "Failure to follow professional advice",
        "Delays caused by third parties, traffic, weather or events beyond reasonable control",
        "Unauthorised account access caused by the user's failure to protect credentials",
        "Independent clinical decisions made by qualified healthcare professionals",
        "Temporary interruption of the website, application or communication services",
      ]} />
      <Para>
        Nothing in these Terms excludes or limits any liability that cannot lawfully be excluded, including rights available to consumers under applicable law.
      </Para>

      <SectionH n="21">Suspension and Termination</SectionH>
      <Para>Suvidha may restrict, suspend or terminate access to an account or service if:</Para>
      <BulletList items={[
        "These Terms are violated.",
        "Payments remain overdue.",
        "Fraud or misuse is suspected.",
        "Staff or service providers face abuse or unsafe conditions.",
        "Continued service creates a legal, clinical or safety risk.",
        "Suspension is required by law or a competent authority.",
      ]} />
      <Para>Termination will not affect payment obligations or rights arising before termination.</Para>

      <SectionH n="22">Changes to These Terms</SectionH>
      <Para>
        Suvidha may update these Terms to reflect changes in services, technology, legal requirements or business practices.
      </Para>
      <Para>
        The updated version will be published on the platform with a revised effective date. Material changes may also be communicated through email, SMS, WhatsApp or application notification.
      </Para>
      <Para>Continued use of the platform after the effective date constitutes acceptance of the updated Terms.</Para>

      <SectionH n="23">Governing Law and Jurisdiction</SectionH>
      <Para>These Terms will be governed by the laws of India.</Para>
      <Para>
        Subject to any rights available under applicable consumer law, courts located in <strong>{JURISDICTION}</strong> will have jurisdiction over disputes arising from these Terms or the use of Suvidha's services.
      </Para>
      <Para>
        The parties should first attempt to resolve disputes through good-faith communication before initiating formal proceedings.
      </Para>

      <SectionH n="24">Grievances and Contact Information</SectionH>
      <Para>Questions, complaints or grievances regarding the platform or services may be directed to:</Para>
      <GrievanceBlock title="📩 Contact & Grievance Officer" />
      <Para>Suvidha will acknowledge and address grievances within the timelines required under applicable law.</Para>
    </div>
  );
}

function RefundContent() {
  return (
    <div>
      <EffectiveDates />
      <Para>
        This Refund and Cancellation Policy applies to services, care plans, consultations, home visits, diagnostic services, medical equipment and other bookings made through the {COMPANY} website, mobile application or authorised support channels.
      </Para>
      <Para>By making a payment, the customer agrees to this policy.</Para>

      <SectionH n="1">General Conditions</SectionH>
      <Para>Refund eligibility depends on:</Para>
      <BulletList items={[
        "The type of service purchased",
        "The time at which cancellation is requested",
        "Whether a healthcare professional has been assigned",
        "Whether the professional has started travelling",
        "Whether the service has commenced",
        "Whether equipment, medicines or consumables have been arranged",
        "Charges already incurred with third-party service providers",
        "Benefits already used under a care plan",
      ]} />
      <Para>
        Cancellation requests must be submitted through the Suvidha application, website, customer-support number, email or another authorised communication channel.
      </Para>
      <Para>The date and time recorded in Suvidha's system will be used to determine refund eligibility.</Para>

      <SectionH n="2">Home Healthcare and Scheduled Services</SectionH>
      <Para>
        This section applies to scheduled doctor visits, nursing services, attendant services, physiotherapy, rehabilitation, health monitoring and similar home-based services.
      </Para>
      <SubHead>Cancellation more than 24 hours before the scheduled service</SubHead>
      <Para>The customer may:</Para>
      <BulletList items={[
        "Receive a full refund; or",
        "Reschedule the service without an additional rescheduling charge.",
      ]} />
      <Para>
        Any payment-processing charge that has already been incurred and cannot be recovered may be deducted, provided it was disclosed to the customer.
      </Para>
      <SubHead>Cancellation between 6 and 24 hours before the scheduled service</SubHead>
      <Para>The customer may:</Para>
      <BulletList items={[
        "Receive an 80% refund; or",
        "Reschedule the service once, subject to availability.",
      ]} />
      <SubHead>Cancellation less than 6 hours before the scheduled service</SubHead>
      <Para>
        The booking fee will ordinarily be non-refundable if a professional has already been assigned or service arrangements have commenced.
      </Para>
      <Para>
        Suvidha may permit one rescheduling request at its discretion, subject to professional availability and any travel or preparation charges already incurred.
      </Para>
      <SubHead>Cancellation after dispatch or commencement</SubHead>
      <Para>
        No refund will be provided after the healthcare professional or service provider has been dispatched, reached the service location or commenced the service.
      </Para>

      <SectionH n="3">Doctor and Teleconsultation Appointments</SectionH>
      <Para>A consultation may be rescheduled or cancelled more than six hours before the scheduled appointment.</Para>
      <Para>No refund will ordinarily be provided when:</Para>
      <BulletList items={[
        "The cancellation is made within six hours of the appointment.",
        "The patient does not attend the appointment.",
        "The patient is unavailable at the scheduled time.",
        "The consultation has commenced.",
        "The doctor has reviewed records or provided professional advice.",
      ]} />
      <Para>If the doctor or Suvidha cancels the appointment, the customer may choose between:</Para>
      <BulletList items={[
        "Rescheduling without additional charge; or",
        "Receiving a full refund for that consultation.",
      ]} />

      <SectionH n="4">Laboratory and Diagnostic Services</SectionH>
      <Para>
        A full refund may be issued when a diagnostic booking is cancelled before a technician is assigned or dispatched.
      </Para>
      <Para>Once a technician has been dispatched, a collection charge may be deducted.</Para>
      <Para>No refund will be available after:</Para>
      <BulletList items={[
        "A sample has been collected.",
        "A diagnostic procedure has commenced.",
        "A laboratory has started processing the sample.",
        "A report has been generated.",
      ]} />
      <Para>
        If the sample becomes unsuitable because of collection, handling or laboratory error, Suvidha or the diagnostic partner may arrange a repeat collection without additional testing charges.
      </Para>
      <Para>
        A repeat test or refund will not apply when the sample cannot be processed because the patient did not follow the communicated preparation instructions.
      </Para>

      <SectionH n="5">Care Plans and Subscriptions</SectionH>
      <SubHead>Cancellation before activation</SubHead>
      <Para>
        A full refund may be issued when a care plan is cancelled before activation and before any benefit or service has been used.
      </Para>
      <SubHead>Cancellation within seven days of activation</SubHead>
      <Para>
        A customer may request cancellation within seven days of activation only if no consultation, home visit, test, emergency coordination, discount, medical review or other plan benefit has been used.
      </Para>
      <Para>
        Where no benefit has been used, the refund may be processed after deducting non-recoverable payment-processing charges, if applicable.
      </Para>
      <SubHead>Cancellation after using a plan benefit</SubHead>
      <Para>Once any benefit under the plan has been used, the subscription fee will ordinarily be non-refundable.</Para>
      <Para>This includes:</Para>
      <BulletList items={[
        "Doctor or nurse consultation",
        "Home visit",
        "Laboratory test",
        "Physiotherapy or rehabilitation session",
        "Emergency coordination",
        "Ambulance coordination",
        "Medical-record review",
        "Plan discount",
        "Hospital assistance",
        "Any other included service",
      ]} />
      <SubHead>Partial usage</SubHead>
      <Para>
        Unused benefits do not have an individual cash value and cannot ordinarily be exchanged for a partial refund.
      </Para>
      <Para>
        In exceptional circumstances, including death, permanent relocation outside the service area or a serious medical condition preventing further use, Suvidha may review the request individually. Supporting documents may be required.
      </Para>

      <SectionH n="6">Monthly Nursing, Attendant and Long-Term Care Services</SectionH>
      <Para>For monthly nursing, attendant or long-term home-care arrangements:</Para>
      <BulletList items={[
        "The initial assessment, registration or placement charge may be non-refundable after staff allocation begins.",
        "The customer must provide the notice period stated in the service agreement.",
        "Charges for completed service days will not be refunded.",
        "Salary, travel or replacement costs already incurred may be deducted.",
        "Any approved balance will be calculated after adjusting outstanding charges.",
      ]} />
      <Para>
        If Suvidha is unable to provide the agreed service or a reasonable replacement, the unused portion may be refunded on a proportionate basis.
      </Para>

      <SectionH n="7">Emergency, Ambulance and Hospital Support</SectionH>
      <Para>Emergency coordination, ambulance services and hospital-assistance bookings become non-refundable once:</Para>
      <BulletList items={[
        "The ambulance or support professional has been dispatched.",
        "Hospital coordination has commenced.",
        "Admission arrangements have been initiated.",
        "Transport or third-party services have been confirmed.",
      ]} />
      <Para>
        If Suvidha cannot arrange the requested service and no third-party cost has been incurred, the amount paid for that service will be refunded.
      </Para>
      <Para>
        Suvidha does not guarantee ambulance response time, hospital-bed availability or admission, as these may depend on third-party providers and circumstances beyond Suvidha's control.
      </Para>

      <SectionH n="8">Medical Equipment Rentals</SectionH>
      <Para>A rental booking may be cancelled for a full refund before the equipment has been prepared or dispatched.</Para>
      <Para>After dispatch, Suvidha may deduct:</Para>
      <BulletList items={[
        "Delivery and pickup charges",
        "Installation charges",
        "Sanitisation or preparation charges",
        "Rental charges for the period of possession",
        "Charges for missing accessories or damage beyond normal wear",
      ]} />
      <Para>
        The refundable security deposit, where applicable, will be processed after the equipment is returned and inspected.
      </Para>
      <Para>Deductions may be made for:</Para>
      <BulletList items={[
        "Physical damage",
        "Missing components",
        "Unauthorised repair or modification",
        "Excessive cleaning requirements",
        "Delayed return",
        "Outstanding rental payments",
      ]} />
      <Para>The security-deposit balance will ordinarily be refunded within <strong>7–10 working days</strong> after inspection.</Para>

      <SectionH n="9">Medical Equipment Purchases</SectionH>
      <Para>Returns of purchased medical equipment will be accepted only when:</Para>
      <BulletList items={[
        "The product delivered is incorrect.",
        "The product is damaged at the time of delivery.",
        "A manufacturing defect is reported within the applicable return or warranty period.",
      ]} />
      <Para>
        The customer should report visible damage or an incorrect item within 48 hours of delivery and provide photographs, videos or other reasonable evidence.
      </Para>
      <Para>For health, hygiene and safety reasons, the following products may be non-returnable once opened or used:</Para>
      <BulletList items={[
        "Personal-use medical devices",
        "Hygiene-related products",
        "Consumables",
        "Catheters and similar products",
        "Masks, gloves and dressings",
        "Customised equipment",
        "Products with broken hygiene seals",
      ]} />
      <Para>
        Manufacturer warranties will apply where provided. Installation, delivery or consumable charges may be non-refundable unless the product supplied was incorrect or defective.
      </Para>

      <SectionH n="10">Medicines and Healthcare Consumables</SectionH>
      <Para>
        Medicines and healthcare consumables will not ordinarily be accepted for return or refund after delivery because of safety, storage and regulatory requirements.
      </Para>
      <Para>A replacement or refund may be considered if:</Para>
      <BulletList items={[
        "The wrong product was delivered.",
        "The product was damaged during delivery.",
        "The product was expired at the time of delivery.",
        "The delivered quantity differed from the confirmed order.",
      ]} />
      <Para>The issue must be reported within 48 hours of delivery with appropriate supporting evidence.</Para>

      <SectionH n="11">Travel, Events and Third-Party Activities</SectionH>
      <Para>
        For devotional travel, wellness events, group activities or other services involving external vendors, the cancellation conditions communicated for that particular activity will apply.
      </Para>
      <Para>Refunds may be subject to deductions for:</Para>
      <BulletList items={[
        "Travel reservations",
        "Accommodation",
        "Venue charges",
        "Transport",
        "Entry tickets",
        "Insurance",
        "Vendor cancellation fees",
        "Other non-recoverable expenses",
      ]} />
      <Para>Where a third-party provider treats an amount as non-refundable, Suvidha may be unable to refund that portion.</Para>

      <SectionH n="12">Non-Refundable Situations</SectionH>
      <Para>A refund will not ordinarily be provided when:</Para>
      <BulletList items={[
        "The service has been completed.",
        "The customer or patient was unavailable.",
        "Incorrect or incomplete information was provided.",
        "Entry to the service location was denied.",
        "The customer refused an appropriate assigned professional without a reasonable service-related cause.",
        "The patient did not follow necessary preparation instructions.",
        "A delay arose from traffic, weather, hospital availability or circumstances beyond reasonable control.",
        "The customer changed their mind after the service commenced.",
        "A plan benefit, promotional benefit or discount was already used.",
        "The request is made outside the applicable cancellation period.",
      ]} />
      <Para>Nothing in this section limits any refund or remedy required under applicable law.</Para>

      <SectionH n="13">Cancellation by Suvidha</SectionH>
      <Para>Suvidha may cancel or reschedule a service because of:</Para>
      <BulletList items={[
        "Professional unavailability",
        "Medical or operational emergencies",
        "Unsafe service conditions",
        "Incorrect booking information",
        "Non-payment",
        "Technical problems",
        "Weather, transport disruption or force majeure",
        "Circumstances making the requested service medically or operationally unsuitable",
      ]} />
      <Para>
        When Suvidha cancels a prepaid service and cannot provide a suitable alternative, the customer will receive a full refund for the undelivered service.
      </Para>

      <SectionH n="14">Refund Processing</SectionH>
      <Para>Approved refunds will be initiated to the original payment method.</Para>
      <Para>
        Refunds are ordinarily initiated within <strong>7–10 working days</strong> after approval. The time required for the amount to appear in the customer's account may vary depending on the bank, card network, UPI provider or payment gateway.
      </Para>
      <Para>Suvidha is not responsible for delays caused by banks or payment-service providers after the refund has been initiated.</Para>
      <Para>Cash payments, where accepted, may be refunded through bank transfer after verification of the customer's details.</Para>

      <SectionH n="15">Duplicate or Failed Transactions</SectionH>
      <Para>If the customer is charged more than once for the same booking, the duplicate payment will be refunded after verification.</Para>
      <Para>When a payment is debited but the booking is not confirmed, customers should contact Suvidha with:</Para>
      <BulletList items={[
        "Registered name and telephone number",
        "Transaction date",
        "Amount paid",
        "Transaction or reference number",
        "Screenshot or payment confirmation, where available",
      ]} />
      <Para>Failed transactions may also be automatically reversed by the bank or payment provider.</Para>

      <SectionH n="16">Promotional Offers and Discounts</SectionH>
      <Para>Promotional codes, discounts, complimentary services and reward benefits:</Para>
      <BulletList items={[
        "Cannot be exchanged for cash.",
        "May not be refundable.",
        "May be subject to separate validity and eligibility conditions.",
        "May be withdrawn if the associated transaction is cancelled or refunded.",
      ]} />
      <Para>Only the amount actually paid by the customer will be considered when calculating a refund.</Para>

      <SectionH n="17">Refund Requests</SectionH>
      <Para>Refund or cancellation requests may be submitted through:</Para>
      <div style={{
        background: "#EFF6FF", border: "1px solid #BFDBFE",
        borderRadius: 14, padding: "16px 20px", marginBottom: 14
      }}>
        <p style={{ margin: 0, fontSize: 13, color: "#374151", lineHeight: 1.9 }}>
          <strong>Website/Application:</strong> My Bookings or Support section<br />
          <strong>Email:</strong> <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: BRAND, fontWeight: 700 }}>{SUPPORT_EMAIL}</a><br />
          <strong>Customer Support:</strong> {SUPPORT_PHONE}
        </p>
      </div>
      <Para>The request should include:</Para>
      <BulletList items={[
        "Customer's registered name",
        "Registered telephone number",
        "Booking or transaction ID",
        "Service or plan purchased",
        "Reason for cancellation",
        "Supporting documents, where required",
      ]} />

      <SectionH n="18">Disputes and Grievances</SectionH>
      <Para>If a customer disagrees with a cancellation or refund decision, they may request a review by contacting:</Para>
      <GrievanceBlock title="📩 Disputes & Grievance Officer" />
      <Para>
        Suvidha will review the booking records, service status, provider expenses and supporting information before communicating its decision. This policy will be interpreted in accordance with applicable Indian law. Nothing in this policy limits the statutory rights available to consumers.
      </Para>
    </div>
  );
}

function PrivacyContent() {
  return (
    <div>
      <EffectiveDates />
      <Para>
        {COMPANY} respects the privacy of senior citizens, family members, caregivers and other users of its services.
      </Para>
      <Para>
        This Privacy Policy explains how <strong>{LEGAL_ENTITY}</strong>, operating under the name "{COMPANY}," collects, uses, stores, processes and shares personal information through its website ({DOMAIN}), mobile application, customer-support channels and elder-care services. The platform is developed and maintained by <strong>{DEV_CO}</strong>.
      </Para>
      <Para>
        By creating an account, submitting information, purchasing a plan, booking a service or otherwise using Suvidha's platform, you acknowledge the practices described in this Privacy Policy.
      </Para>

      <SectionH n="1">Scope of This Policy</SectionH>
      <Para>This policy applies to personal information collected through:</Para>
      <BulletList items={[
        "The Suvidha Elder Care website",
        "The Suvidha mobile application",
        "Account registration and login",
        "Service bookings and care-plan subscriptions",
        "Telephone, SMS, email and WhatsApp communication",
        "Home healthcare and care-coordination services",
        "Medical-document uploads",
        "Customer-support and grievance requests",
        "Surveys, feedback and promotional activities",
        "Healthcare professionals and authorised service partners",
      ]} />
      <Para>
        This policy does not govern the independent privacy practices of hospitals, doctors, laboratories, pharmacies, payment providers or other third parties operating outside Suvidha's control.
      </Para>

      <SectionH n="2">Information We May Collect</SectionH>
      <Para>Depending on the services used, Suvidha may collect the following information.</Para>
      <SubHead>Personal and contact information</SubHead>
      <BulletList items={[
        "Full name",
        "Age and date of birth",
        "Gender",
        "Telephone number",
        "Email address",
        "Residential and service address",
        "Emergency contact information",
        "Relationship between the account holder and care recipient",
        "Profile photograph, where provided",
      ]} />
      <SubHead>Account information</SubHead>
      <BulletList items={[
        "Account identifiers",
        "Login and authentication information",
        "OTP verification records",
        "User preferences",
        "Subscription and membership details",
        "Communication preferences",
      ]} />
      <Para>Suvidha does not request users to share passwords, banking PINs or OTPs with staff members.</Para>
      <SubHead>Health and medical information</SubHead>
      <Para>Where required for providing or coordinating care, Suvidha may collect:</Para>
      <BulletList items={[
        "Existing medical conditions",
        "Current medications",
        "Allergies",
        "Medical history",
        "Prescriptions",
        "Laboratory reports",
        "Diagnostic reports",
        "Case sheets and discharge summaries",
        "Doctor recommendations",
        "Nursing and physiotherapy records",
        "Mobility and rehabilitation requirements",
        "Care instructions",
        "Emergency-related information",
        "Records of completed sessions and follow-ups",
        "Other information voluntarily provided for care coordination",
      ]} />
      <Para>
        Health and medical information will be treated as confidential and accessed only for legitimate service, care, administrative, legal or safety purposes.
      </Para>
      <SubHead>Booking and service information</SubHead>
      <BulletList items={[
        "Services requested",
        "Appointment dates and times",
        "Assigned healthcare professional or caregiver",
        "Service location",
        "Service completion status",
        "Care-plan benefits used",
        "Cancellations and refund requests",
        "Feedback, complaints and support history",
      ]} />
      <SubHead>Payment and transaction information</SubHead>
      <Para>Suvidha may collect:</Para>
      <BulletList items={[
        "Transaction amount",
        "Invoice information",
        "Payment status",
        "Transaction reference number",
        "Refund details",
        "Limited payment-method information provided by the payment gateway",
      ]} />
      <Para>
        Complete card details, UPI PINs and banking passwords are generally processed by authorised payment providers and are not intended to be stored by Suvidha.
      </Para>
      <SubHead>Device and technical information</SubHead>
      <Para>When users access the website or application, Suvidha may automatically collect:</Para>
      <BulletList items={[
        "Device type",
        "Operating system",
        "Browser type",
        "Internet Protocol address",
        "Application version",
        "Login time",
        "Platform activity",
        "Error and diagnostic information",
        "Cookie and analytics information",
      ]} />
      <SubHead>Location information</SubHead>
      <Para>Suvidha may collect an address or approximate location to:</Para>
      <BulletList items={[
        "Confirm service availability",
        "Assign nearby professionals",
        "Arrange home visits",
        "Coordinate ambulances or transport",
        "Deliver medical equipment",
        "Provide location-based assistance",
      ]} />
      <Para>Precise device location will be collected only when required, permitted and enabled by the user.</Para>

      <SectionH n="3">Information Provided by Family Members</SectionH>
      <Para>
        A family member may create an account, book services or submit information on behalf of an elderly parent, spouse, relative or another care recipient.
      </Para>
      <Para>The family member confirms that:</Para>
      <BulletList items={[
        "They are authorised to provide the information.",
        "The information is accurate to the best of their knowledge.",
        "The care recipient has been informed where reasonably possible.",
        "Necessary consent has been obtained for sharing medical information.",
      ]} />
      <Para>
        Suvidha may request additional confirmation or documentation when necessary to protect the care recipient's privacy.
      </Para>

      <SectionH n="4">How We Use Personal Information</SectionH>
      <Para>Suvidha may process personal information to:</Para>
      <BulletList items={[
        "Create and manage user accounts",
        "Verify identity and contact information",
        "Provide requested services",
        "Assess care requirements",
        "Arrange doctor, nurse, caregiver or physiotherapy visits",
        "Coordinate laboratory tests and teleconsultations",
        "Manage care-plan subscriptions",
        "Arrange hospital, ambulance and transport assistance",
        "Provide or coordinate medical equipment",
        "Maintain medical and service history",
        "Share relevant information with authorised professionals",
        "Communicate appointments and service updates",
        "Process payments, cancellations and refunds",
        "Send medication, appointment or care reminders",
        "Respond to questions, complaints and emergencies",
        "Improve the website, application and services",
        "Detect fraud, misuse and security incidents",
        "Maintain operational and financial records",
        "Comply with legal and regulatory requirements",
        "Establish, exercise or defend legal claims",
        "Protect users, professionals and the public",
      ]} />

      <SectionH n="5">Consent and Authorisation</SectionH>
      <Para>Where consent is required, Suvidha will seek consent before processing personal information for the stated purpose.</Para>
      <Para>
        Consent may be provided electronically, verbally, in writing or through an authorised family member, depending on the service and circumstances.
      </Para>
      <Para>Users may withdraw consent by contacting Suvidha. Withdrawal will not affect processing already lawfully completed.</Para>
      <Para>Withdrawing consent may prevent Suvidha from continuing services that depend on the relevant information.</Para>

      <SectionH n="6">Medical Records and Professional Access</SectionH>
      <Para>
        Medical documents and care records may be accessible to authorised Suvidha staff, assigned doctors, nurses, physiotherapists, care coordinators and relevant healthcare partners only to the extent necessary for providing or coordinating services.
      </Para>
      <Para>Professionals may use the information to:</Para>
      <BulletList items={[
        "Review the care recipient's condition",
        "Record observations",
        "Prepare care instructions",
        "Maintain medication information",
        "Schedule appropriate sessions",
        "Coordinate follow-ups",
        "Respond to health or safety concerns",
      ]} />
      <Para>
        The Suvidha platform does not automatically prescribe medicines. Medication prescriptions or modifications must be made or approved by an appropriately qualified healthcare professional.
      </Para>

      <SectionH n="7">When We May Share Information</SectionH>
      <Para>Suvidha may share necessary information with:</Para>
      <BulletList items={[
        "Doctors and medical professionals",
        "Nurses, attendants and caregivers",
        "Physiotherapists and rehabilitation professionals",
        "Hospitals and emergency service providers",
        "Laboratories and diagnostic centres",
        "Pharmacies and medicine-delivery providers",
        "Medical-equipment suppliers",
        "Ambulance and transport providers",
        "Payment gateways and banking partners",
        "Cloud hosting and technology providers",
        "SMS, email, telephone and WhatsApp communication providers",
        "Professional advisers, auditors and insurers",
        "Government authorities, regulators or law-enforcement agencies where legally required",
      ]} />
      <Para>Only the information reasonably required for the relevant service should be shared.</Para>
      <Para>Suvidha does not sell patient medical records or personal information to third parties for independent advertising purposes.</Para>

      <SectionH n="8">Emergency Disclosure</SectionH>
      <Para>
        In an emergency, Suvidha may share relevant information with doctors, hospitals, ambulance providers, emergency contacts or authorities where reasonably necessary to protect the care recipient's life, health or safety.
      </Para>
      <Para>Where practical, Suvidha will inform the user or authorised family member about such disclosure.</Para>

      <SectionH n="9">Marketing Communication</SectionH>
      <Para>With the user's consent, Suvidha may send information about:</Para>
      <BulletList items={[
        "Care plans",
        "New services",
        "Health programmes",
        "Events and activities",
        "Promotional offers",
        "Educational health content",
      ]} />
      <Para>Users may opt out of promotional communication by:</Para>
      <BulletList items={[
        "Using an unsubscribe option",
        "Changing communication preferences",
        "Contacting customer support",
      ]} />
      <Para>
        Opting out of promotions will not stop essential service communications, including OTPs, appointment confirmations, payment updates and emergency-related messages.
      </Para>

      <SectionH n="10">Cookies and Similar Technologies</SectionH>
      <Para>The website and application may use cookies, software development kits and similar technologies to:</Para>
      <BulletList items={[
        "Keep users signed in",
        "Remember preferences",
        "Improve performance",
        "Understand how features are used",
        "Detect security issues",
        "Measure website and campaign effectiveness",
      ]} />
      <Para>
        Users may restrict cookies through browser settings. Some website features may not function correctly if essential cookies are disabled.
      </Para>
      <Para>A separate Cookies Policy may provide additional details.</Para>

      <SectionH n="11">Data Storage and Retention</SectionH>
      <Para>Personal information may be stored using Suvidha's systems and authorised cloud or technology-service providers.</Para>
      <Para>Suvidha will retain information only for as long as reasonably necessary to:</Para>
      <BulletList items={[
        "Provide services",
        "Maintain medical and service history",
        "Complete financial and administrative obligations",
        "Resolve complaints and disputes",
        "Meet contractual obligations",
        "Comply with legal and regulatory requirements",
      ]} />
      <Para>
        When information is no longer required, Suvidha may securely delete, anonymise or restrict access to it, subject to applicable retention requirements.
      </Para>

      <SectionH n="12">Data Security</SectionH>
      <Para>Suvidha uses reasonable administrative, technical and organisational measures intended to protect personal information from:</Para>
      <BulletList items={[
        "Unauthorised access",
        "Loss or misuse",
        "Unlawful disclosure",
        "Alteration",
        "Destruction",
        "Accidental damage",
      ]} />
      <Para>
        These measures may include controlled access, authentication, staff confidentiality obligations, backups, monitoring and other appropriate safeguards.
      </Para>
      <Para>
        No website, application or electronic-storage system can be guaranteed to be completely secure. Users should protect their devices, login information and OTPs and should notify Suvidha immediately if unauthorised account activity is suspected.
      </Para>

      <SectionH n="13">Data Breach Response</SectionH>
      <Para>If Suvidha becomes aware of a personal-data breach, it will investigate the incident and take reasonable steps to limit its impact.</Para>
      <Para>Affected users and relevant authorities will be notified where required under applicable law.</Para>
      <Para>Users should report suspected privacy or security incidents immediately through the contact details provided below.</Para>

      <SectionH n="14">User Rights</SectionH>
      <Para>Subject to applicable law, users may request to:</Para>
      <BulletList items={[
        "Know what personal information is being processed",
        "Obtain a summary of relevant processing activities",
        "Correct inaccurate or incomplete information",
        "Update personal and contact information",
        "Request deletion of information that is no longer required",
        "Withdraw consent where processing is based on consent",
        "Raise a complaint regarding the handling of personal information",
        "Nominate another person to exercise applicable rights in the event of death or incapacity",
      ]} />
      <Para>
        Some information may need to be retained for medical continuity, legal compliance, financial records, dispute resolution or other lawful purposes.
      </Para>
      <Para>Suvidha may verify the identity and authority of the person submitting a request before acting upon it.</Para>

      <SectionH n="15">Account and Data Deletion</SectionH>
      <Para>Users may request account deletion through the application or by contacting Suvidha.</Para>
      <Para>Account deletion may result in:</Para>
      <BulletList items={[
        "Loss of access to bookings and subscriptions",
        "Removal of profile information",
        "Inability to view medical and service history",
        "Discontinuation of certain services and reminders",
      ]} />
      <Para>Some records may be retained where required for legal, financial, clinical, safety or dispute-resolution purposes.</Para>
      <Para>Deleting the application from a device does not automatically delete the user's account or stored information.</Para>

      <SectionH n="16">Children's Information</SectionH>
      <Para>Suvidha's services are primarily intended for adults and senior citizens.</Para>
      <Para>
        If information relating to a person under 18 years of age is required for a particular service, it must be submitted by or with the consent of a parent or lawful guardian.
      </Para>
      <Para>Suvidha does not knowingly permit minors to independently create accounts or purchase services.</Para>

      <SectionH n="17">International Processing</SectionH>
      <Para>Some technology, cloud, communication or service providers may process information outside India.</Para>
      <Para>
        Where cross-border processing occurs, Suvidha will take reasonable measures to ensure that the information is handled in accordance with applicable law and appropriate contractual or security safeguards.
      </Para>

      <SectionH n="18">Third-Party Platforms and Links</SectionH>
      <Para>
        The platform may contain links to hospitals, payment gateways, laboratories, social-media platforms or other third-party services.
      </Para>
      <Para>
        Suvidha is not responsible for the privacy practices, security or content of independently operated third-party platforms. Users should review the relevant third party's privacy policy before providing information.
      </Para>

      <SectionH n="19">Changes to This Privacy Policy</SectionH>
      <Para>Suvidha may update this Privacy Policy to reflect changes in:</Para>
      <BulletList items={[
        "Services and platform features",
        "Data-processing practices",
        "Technology",
        "Legal or regulatory requirements",
        "Security standards",
      ]} />
      <Para>
        The revised policy will be published with an updated effective date. Material changes may also be communicated through the application, email, SMS or another appropriate channel.
      </Para>

      <SectionH n="20">Grievances and Contact Information</SectionH>
      <Para>Questions, correction requests, deletion requests, complaints or privacy concerns may be submitted to:</Para>
      <GrievanceBlock title="📩 Privacy Grievance Officer" />
      <Para>Suvidha will acknowledge and process privacy grievances within the timelines required under applicable law.</Para>

      <SectionH n="21">Governing Law</SectionH>
      <Para>This Privacy Policy will be governed by applicable laws of India.</Para>
      <Para>
        Subject to rights available under applicable law, courts located in {JURISDICTION} will have jurisdiction over disputes relating to this Privacy Policy.
      </Para>
    </div>
  );
}

function CookiesContent() {
  return (
    <div>
      <EffectiveDates />
      <Para>
        This Cookies Policy explains how <strong>{LEGAL_ENTITY}</strong>, operating under the name "{COMPANY}," uses cookies and similar technologies on its website ({DOMAIN}) and mobile application, developed and maintained by <strong>{DEV_CO}</strong>.
      </Para>
      <Para>This policy should be read together with {COMPANY}'s Privacy Policy and Terms and Conditions.</Para>

      <SectionH n="1">What Are Cookies?</SectionH>
      <Para>Cookies are small text files placed on a user's device when they visit a website.</Para>
      <Para>Cookies can help a website:</Para>
      <BulletList items={[
        "Function correctly",
        "Keep users signed in",
        "Remember user preferences",
        "Improve security",
        "Understand how visitors use the website",
        "Measure website performance",
        "Display relevant communication or advertising",
      ]} />
      <Para>Some cookies remain on the device only during the browsing session, while others may remain for a specified period or until deleted.</Para>

      <SectionH n="2">Scope of This Policy</SectionH>
      <Para>This policy applies to cookies and similar technologies used through:</Para>
      <BulletList items={[
        "The Suvidha Elder Care website",
        "Account registration and login pages",
        "Service-booking pages",
        "Care-plan and subscription pages",
        "Payment and checkout pages",
        "Contact and enquiry forms",
        "The Suvidha mobile application",
        "Embedded third-party services",
      ]} />
      <Para>Similar technologies may include:</Para>
      <BulletList items={[
        "Browser local storage",
        "Pixels",
        "Tags",
        "Software development kits",
        "Mobile-device identifiers",
        "Analytics tools",
        "Session-storage technologies",
      ]} />

      <SectionH n="3">Types of Cookies We May Use</SectionH>
      <SubHead>Strictly Necessary Cookies</SubHead>
      <Para>These cookies are required for the website or application to function and ordinarily cannot be disabled through Suvidha's cookie settings.</Para>
      <Para>They may be used to:</Para>
      <BulletList items={[
        "Maintain secure user sessions",
        "Enable account login",
        "Process booking requests",
        "Remember items during checkout",
        "Support payment processing",
        "Prevent fraud and misuse",
        "Maintain security settings",
        "Save cookie-consent choices",
        "Distribute website traffic reliably",
      ]} />
      <Para>Disabling these cookies through browser settings may prevent important parts of the website from working.</Para>
      <SubHead>Preference Cookies</SubHead>
      <Para>Preference cookies allow the platform to remember choices made by the user.</Para>
      <Para>They may remember:</Para>
      <BulletList items={[
        "Preferred language",
        "Country or region",
        "Selected service location",
        "Display preferences",
        "Login preferences",
        "Accessibility settings",
        "Previously selected services",
      ]} />
      <Para>These cookies help provide a more convenient and personalised experience.</Para>
      <SubHead>Functional Cookies</SubHead>
      <Para>Functional cookies support enhanced features that may not be essential to the basic operation of the website.</Para>
      <Para>They may support:</Para>
      <BulletList items={[
        "Chat or customer-support features",
        "Appointment calendars",
        "Maps and location services",
        "Embedded videos",
        "Social-media content",
        "Feedback tools",
        "Communication widgets",
      ]} />
      <Para>If these cookies are disabled, some optional features may not operate correctly.</Para>
      <SubHead>Analytics and Performance Cookies</SubHead>
      <Para>These cookies help Suvidha understand how visitors use the platform.</Para>
      <Para>They may collect information such as:</Para>
      <BulletList items={[
        "Number of visitors",
        "Pages visited",
        "Time spent on a page",
        "Navigation between pages",
        "Device and browser type",
        "General geographic region",
        "Application or website errors",
        "Source through which the visitor reached the website",
      ]} />
      <Para>
        This information may be used to improve website performance, user experience, content and service navigation. Where possible, analytics information may be aggregated or pseudonymised.
      </Para>
      <SubHead>Advertising and Marketing Cookies</SubHead>
      <Para>With the user's consent, advertising and marketing cookies may be used to:</Para>
      <BulletList items={[
        "Measure advertising-campaign performance",
        "Understand which campaigns generate enquiries",
        "Limit repeated advertisements",
        "Create audiences for relevant advertising",
        "Measure conversions",
        "Display relevant advertisements on third-party platforms",
      ]} />
      <Para>These cookies may be placed by Suvidha or authorised advertising partners.</Para>
      <Para>Suvidha should not use medical records, prescriptions or confidential health information for targeted advertising.</Para>

      <SectionH n="4">First-Party and Third-Party Cookies</SectionH>
      <SubHead>First-party cookies</SubHead>
      <Para>First-party cookies are placed directly by the Suvidha Elder Care website. They may support login, security, bookings, preferences and website functionality.</Para>
      <SubHead>Third-party cookies</SubHead>
      <Para>Third-party cookies are placed by external service providers whose technology is integrated into the website.</Para>
      <Para>These providers may include:</Para>
      <BulletList items={[
        "Website analytics providers",
        "Payment gateways",
        "Map and location providers",
        "Video-hosting services",
        "Social-media platforms",
        "Customer-support tools",
        "Advertising platforms",
        "Security and fraud-prevention services",
      ]} />
      <Para>
        Third parties may process information under their own privacy and cookie policies. Suvidha does not directly control the duration or operation of every third-party cookie.
      </Para>

      <SectionH n="5">Payment Services</SectionH>
      <Para>When a user makes a payment, the payment gateway may use cookies or similar technologies to:</Para>
      <BulletList items={[
        "Process the transaction",
        "Authenticate the payment",
        "Prevent fraud",
        "Maintain transaction security",
        "Remember the payment session",
      ]} />
      <Para>Payment providers operate under their own privacy and security policies.</Para>
      <Para>Suvidha does not use cookies to store UPI PINs, card PINs, banking passwords or complete payment credentials.</Para>

      <SectionH n="6">Cookies and Healthcare Information</SectionH>
      <Para>
        Suvidha may use essential session and security technologies when users access medical records, prescriptions, appointments or care information.
      </Para>
      <Para>These technologies may help:</Para>
      <BulletList items={[
        "Authenticate the user",
        "Maintain a secure session",
        "Prevent unauthorised access",
        "Protect uploaded documents",
        "Record essential security activity",
      ]} />
      <Para>Suvidha should not place confidential medical information directly inside advertising or analytics cookies.</Para>

      <SectionH n="7">Mobile Application Technologies</SectionH>
      <Para>
        Mobile applications may not use browser cookies in the same way as websites. However, the Suvidha mobile application may use similar technologies, including:
      </Para>
      <BulletList items={[
        "Device identifiers",
        "Application storage",
        "Authentication tokens",
        "Analytics software development kits",
        "Crash-reporting tools",
        "Push-notification identifiers",
        "Security and fraud-prevention technologies",
      ]} />
      <Para>
        These technologies may be used to keep users signed in, send service notifications, improve application performance and protect accounts.
      </Para>
      <Para>Users can manage certain permissions through the settings of their mobile device.</Para>

      <SectionH n="8">Cookie Consent</SectionH>
      <Para>When legally required, Suvidha will request the user's consent before placing non-essential cookies.</Para>
      <Para>Users should be given the option to:</Para>
      <BulletList items={[
        "Accept all cookies",
        "Reject non-essential cookies",
        "Customise cookie preferences",
        "Change or withdraw consent later",
      ]} />
      <Para>Strictly necessary cookies may remain active because they are required for security and core website functionality.</Para>
      <Para>Rejecting optional cookies will not prevent users from accessing the basic website, although some features may be limited.</Para>

      <SectionH n="9">Managing Cookie Preferences</SectionH>
      <Para>Users may manage cookies through:</Para>
      <BulletList items={[
        "Suvidha's cookie-consent banner",
        "The Cookie Settings option on the website",
        "Browser privacy settings",
        "Mobile-device settings",
        "Third-party advertising preferences",
      ]} />
      <Para>Most browsers allow users to:</Para>
      <BulletList items={[
        "View stored cookies",
        "Delete cookies",
        "Block all cookies",
        "Block third-party cookies",
        "Receive notifications before a cookie is stored",
        "Clear cookies when the browser closes",
      ]} />
      <Para>Deleting cookies may sign the user out and remove saved preferences.</Para>

      <SectionH n="10">Browser Instructions</SectionH>
      <Para>Cookie controls are generally available in the privacy or security settings of commonly used browsers, including:</Para>
      <BulletList items={[
        "Google Chrome",
        "Microsoft Edge",
        "Mozilla Firefox",
        "Apple Safari",
        "Samsung Internet",
      ]} />
      <Para>Users should consult the help section of their browser for current instructions.</Para>

      <SectionH n="11">Advertising Preferences</SectionH>
      <Para>Users may be able to manage personalised advertising through the privacy settings provided by relevant advertising platforms.</Para>
      <Para>
        Disabling advertising cookies does not necessarily prevent advertisements from being displayed. It may make the advertisements less personalised and may limit Suvidha's ability to measure campaign performance.
      </Para>

      <SectionH n="12">Do Not Track Signals</SectionH>
      <Para>Some browsers offer a "Do Not Track" setting.</Para>
      <Para>
        There is currently no universally accepted technical standard for responding to all Do Not Track signals. Suvidha will respond to browser and device privacy controls where required and technically supported.
      </Para>

      <SectionH n="13">Cookie Duration</SectionH>
      <Para>Cookies may be:</Para>
      <SubHead>Session cookies</SubHead>
      <Para>These cookies are ordinarily deleted when the browser is closed or the application session ends.</Para>
      <SubHead>Persistent cookies</SubHead>
      <Para>These cookies remain on the device for a specified period or until manually deleted.</Para>
      <Para>The duration of each cookie depends on its purpose and the settings of Suvidha or the relevant third-party provider.</Para>
      <Para>Suvidha should retain cookies only for as long as reasonably necessary for their stated purpose.</Para>

      <SectionH n="14">Updates to This Cookies Policy</SectionH>
      <Para>Suvidha may update this policy to reflect:</Para>
      <BulletList items={[
        "Changes in website or application features",
        "New analytics or communication tools",
        "Changes in advertising practices",
        "Changes in third-party service providers",
        "Legal or regulatory requirements",
      ]} />
      <Para>The revised policy will be published with an updated effective date.</Para>
      <Para>Where required, users may be asked to provide consent again after a material change.</Para>

      <SectionH n="15">Contact Information</SectionH>
      <Para>Questions about Suvidha's use of cookies or similar technologies may be directed to:</Para>
      <GrievanceBlock title="📩 Contact Us" />
    </div>
  );
}

function DisclaimerContent() {
  return (
    <div>
      <EffectiveDates />
      <Para>
        The information and services available through the {COMPANY} website ({DOMAIN}), mobile application and authorised communication channels are intended to support elder-care coordination and access to healthcare and assistance services. This website is designed and maintained by <strong>{DEV_CO}</strong>.
      </Para>
      <Para>By accessing the platform or using Suvidha's services, you acknowledge and agree to this Disclaimer.</Para>

      <SectionH n="1">General Information Disclaimer</SectionH>
      <Para>
        Information available on the Suvidha platform, including articles, blogs, frequently asked questions, service descriptions, health tips and educational materials, is provided for general informational purposes only.
      </Para>
      <Para>
        Although reasonable efforts are made to keep the information accurate and current, Suvidha does not guarantee that all information will always be complete, error-free or suitable for every individual.
      </Para>
      <Para>Users should not rely solely on general website or application content when making medical, financial or personal-care decisions.</Para>

      <SectionH n="2">Not a Substitute for Medical Advice</SectionH>
      <Para>Information provided through the website or application is not a substitute for:</Para>
      <BulletList items={[
        "Medical examination",
        "Diagnosis",
        "Prescription",
        "Treatment",
        "Hospital care",
        "Emergency medical intervention",
        "Advice from a qualified healthcare professional",
      ]} />
      <Para>
        Users should consult an appropriately qualified doctor or healthcare professional regarding symptoms, medical conditions, medication, treatment or changes in health.
      </Para>
      <Para>Users should not start, stop or modify medication based solely on information available through the Suvidha platform.</Para>

      <SectionH n="3">No Automatic Prescription or Diagnosis</SectionH>
      <Para>
        The Suvidha platform may allow users to upload prescriptions, laboratory reports, discharge summaries, medical histories or medication information.
      </Para>
      <Para>Uploading a document does not automatically create:</Para>
      <BulletList items={[
        "A medical diagnosis",
        "A valid prescription",
        "A treatment recommendation",
        "A medication change",
        "An emergency response",
        "A doctor-patient consultation",
      ]} />
      <Para>
        Any diagnosis, prescription or medication modification must be made or approved by an appropriately qualified healthcare professional. The platform should not automatically prescribe medicines.
      </Para>

      <SectionH n="4">Emergency Disclaimer</SectionH>
      <Para>
        The Suvidha platform is not a replacement for government emergency services, hospital emergency departments or immediate medical intervention.
      </Para>
      <Para>
        In a medical emergency, including chest pain, breathing difficulty, loss of consciousness, stroke symptoms, uncontrolled bleeding, serious injury or another life-threatening condition, contact the appropriate emergency service or visit the nearest hospital immediately.
      </Para>
      <Para>Emergency, ambulance and hospital coordination provided by Suvidha may depend on:</Para>
      <BulletList items={[
        "Professional availability",
        "Ambulance availability",
        "Service location",
        "Traffic and road conditions",
        "Hospital capacity",
        "Acceptance by the hospital",
        "Communication-network availability",
        "Other circumstances beyond Suvidha's control",
      ]} />
      <Para>Suvidha does not guarantee ambulance arrival times, hospital-bed availability, admission or a particular emergency outcome.</Para>

      <SectionH n="5">Healthcare Outcomes</SectionH>
      <Para>The results of healthcare, rehabilitation, nursing, physiotherapy and elder-care services vary between individuals.</Para>
      <Para>Outcomes may depend on:</Para>
      <BulletList items={[
        "Age and overall health",
        "Existing medical conditions",
        "Severity and duration of the condition",
        "Accuracy of information provided",
        "Adherence to professional advice",
        "Frequency and continuity of services",
        "Medication compliance",
        "Individual response to care or treatment",
        "Circumstances outside Suvidha's control",
      ]} />
      <Para>Suvidha does not guarantee recovery, improvement within a particular period or any specific clinical result.</Para>

      <SectionH n="6">Professional Services</SectionH>
      <Para>
        Doctors, nurses, physiotherapists, caregivers, laboratory professionals and other service providers are responsible for exercising their own professional judgement within their qualifications and permitted scope of practice.
      </Para>
      <Para>Depending on the selected service, a professional may be:</Para>
      <BulletList items={[
        "Employed or engaged by Suvidha",
        "Associated with a partner organisation",
        "An independent professional",
        "Assigned through a third-party service provider",
      ]} />
      <Para>
        Professional advice may differ depending on the individual's condition and the information available at the time of consultation.
      </Para>
      <Para>Users should seek a second opinion where they have concerns about a diagnosis, prescription or recommended treatment.</Para>

      <SectionH n="7">User-Provided Information</SectionH>
      <Para>
        The accuracy and suitability of a service may depend on the information supplied by the patient, family member or authorised representative.
      </Para>
      <Para>Suvidha is not responsible for consequences resulting from:</Para>
      <BulletList items={[
        "Incorrect or incomplete medical information",
        "Failure to disclose allergies",
        "Failure to disclose current medication",
        "Outdated prescriptions or reports",
        "Missing medical history",
        "Incorrect address or contact details",
        "Failure to communicate a change in the patient's condition",
        "Documents uploaded without proper authority",
      ]} />
      <Para>Users are responsible for verifying that the information provided is accurate and current.</Para>

      <SectionH n="8">Medical Records and Family Updates</SectionH>
      <Para>
        Medical records, appointment histories, service notes and family updates made available through the application are intended to support care coordination.
      </Para>
      <Para>The information displayed may not represent a complete hospital or clinical record unless expressly stated.</Para>
      <Para>Updates may occasionally be delayed because of:</Para>
      <BulletList items={[
        "Professional availability",
        "Manual review",
        "Connectivity issues",
        "Pending laboratory reports",
        "Third-party system delays",
        "Technical maintenance",
      ]} />
      <Para>Urgent concerns should be communicated directly to the assigned professional, Suvidha support team or an appropriate emergency service.</Para>

      <SectionH n="9">Third-Party Services</SectionH>
      <Para>Suvidha may coordinate services involving independent:</Para>
      <BulletList items={[
        "Doctors and healthcare professionals",
        "Hospitals",
        "Laboratories and diagnostic centres",
        "Pharmacies",
        "Ambulance providers",
        "Medical-equipment suppliers",
        "Payment gateways",
        "Transport providers",
        "Caregivers and home-service partners",
        "Travel and event vendors",
      ]} />
      <Para>
        Third-party services may be governed by their own terms, privacy policies, professional standards, availability and cancellation rules.
      </Para>
      <Para>
        To the extent permitted by law, Suvidha is not responsible for the independent acts, omissions, professional decisions or service interruptions of third-party providers outside Suvidha's reasonable control. Nothing in this Disclaimer limits any responsibility that cannot legally be excluded.
      </Para>

      <SectionH n="10">Medical Equipment</SectionH>
      <Para>Medical equipment displayed through the platform must be used only for its intended purpose and in accordance with:</Para>
      <BulletList items={[
        "Manufacturer instructions",
        "Safety instructions",
        "Professional medical advice",
        "Installation guidance",
        "Rental or purchase conditions",
      ]} />
      <Para>
        Suvidha does not guarantee that a particular device is suitable for an individual unless its use has been recommended or confirmed by a qualified professional.
      </Para>
      <Para>Users should not attempt unauthorised repair, modification or operation of medical equipment.</Para>
      <Para>Any malfunction, damage or safety concern should be reported immediately.</Para>

      <SectionH n="11">Laboratory and Diagnostic Information</SectionH>
      <Para>Laboratory and diagnostic reports should be interpreted by a qualified healthcare professional.</Para>
      <Para>Reference ranges, measurements and results may vary depending on:</Para>
      <BulletList items={[
        "Laboratory method",
        "Equipment used",
        "Patient preparation",
        "Timing of collection",
        "Medical history",
        "Other clinical factors",
      ]} />
      <Para>A test result displayed or delivered through the platform does not independently constitute a diagnosis.</Para>

      <SectionH n="12">Service Availability</SectionH>
      <Para>Services, professionals, care plans and response times may vary based on location and availability.</Para>
      <Para>Displaying a service on the website or application does not guarantee that the service will be available:</Para>
      <BulletList items={[
        "In every geographic area",
        "At every requested time",
        "For every medical condition",
        "Through every care plan",
        "At the same price in every location",
      ]} />
      <Para>A booking is confirmed only after Suvidha communicates confirmation through an authorised channel.</Para>

      <SectionH n="13">Pricing and Plan Information</SectionH>
      <Para>Suvidha makes reasonable efforts to display accurate pricing, plan benefits and service information.</Para>
      <Para>However, pricing or content may occasionally contain an error or may require revision because of:</Para>
      <BulletList items={[
        "Changes in third-party charges",
        "Tax changes",
        "Professional availability",
        "Location-based costs",
        "Technical errors",
        "Changes in plan benefits",
      ]} />
      <Para>
        Where a material error is identified before service delivery, Suvidha will inform the customer and provide an opportunity to confirm or cancel the affected booking.
      </Para>

      <SectionH n="14">Insurance and Reimbursement</SectionH>
      <Para>
        Suvidha does not guarantee that a service, consultation, equipment purchase, care plan or healthcare expense will be covered or reimbursed by an insurance provider.
      </Para>
      <Para>Users are responsible for confirming:</Para>
      <BulletList items={[
        "Policy eligibility",
        "Coverage limits",
        "Documentation requirements",
        "Cashless treatment availability",
        "Reimbursement procedures",
      ]} />
      <Para>
        Any insurance-related assistance provided by Suvidha is for coordination purposes and does not constitute a guarantee of claim approval.
      </Para>

      <SectionH n="15">Testimonials and Reviews</SectionH>
      <Para>Testimonials, reviews and care experiences displayed on the platform represent the personal experiences of individual users.</Para>
      <Para>They do not guarantee that another patient or family will receive the same result.</Para>
      <Para>Testimonials may be edited for grammar, clarity or length, but should not be altered in a way that materially changes the customer's experience.</Para>

      <SectionH n="16">External Links</SectionH>
      <Para>
        The website or application may contain links to external websites, payment services, hospitals, laboratories, social-media platforms or other third-party resources.
      </Para>
      <Para>
        These links are provided for convenience. Suvidha does not control and is not responsible for the content, availability, privacy practices or security of independently operated websites.
      </Para>
      <Para>Users should review the relevant third party's terms and privacy policy before using its services.</Para>

      <SectionH n="17">Travel, Events and Group Activities</SectionH>
      <Para>
        Participation in devotional travel, wellness programmes, outings or group activities may involve risks associated with travel, weather, health conditions, public places and third-party vendors.
      </Para>
      <Para>Participants should disclose relevant medical conditions and obtain professional medical advice before participating where necessary.</Para>
      <Para>
        Suvidha may modify or cancel an activity because of safety, weather, participation levels, vendor availability or circumstances beyond reasonable control.
      </Para>

      <SectionH n="18">Technology Disclaimer</SectionH>
      <Para>Suvidha does not guarantee that the website or application will always be:</Para>
      <BulletList items={[
        "Available without interruption",
        "Free from errors",
        "Compatible with every device",
        "Free from delays caused by communication networks",
        "Protected from every possible cybersecurity threat",
      ]} />
      <Para>
        Suvidha may temporarily restrict access for maintenance, security updates, system improvements or technical problems.
      </Para>
      <Para>Users are responsible for maintaining a secure device, protecting login credentials and installing appropriate software updates.</Para>

      <SectionH n="19">Limitation of Liability</SectionH>
      <Para>
        To the maximum extent permitted by applicable law, Suvidha will not be liable for indirect, incidental, special or consequential loss resulting from:
      </Para>
      <BulletList items={[
        "Reliance on general platform information",
        "Failure to seek timely medical attention",
        "Incomplete or incorrect user information",
        "Failure to follow professional advice",
        "Service delays beyond reasonable control",
        "Third-party service interruptions",
        "Unauthorised account access caused by compromised user credentials",
        "Temporary platform unavailability",
      ]} />
      <Para>Nothing in this Disclaimer excludes statutory consumer rights or liability that cannot legally be excluded.</Para>

      <SectionH n="20">Changes to This Disclaimer</SectionH>
      <Para>
        Suvidha may update this Disclaimer to reflect changes in services, platform features, professional practices or legal requirements.
      </Para>
      <Para>The revised Disclaimer will be published with an updated effective date.</Para>

      <SectionH n="21">Contact Information</SectionH>
      <Para>Questions regarding this Disclaimer may be directed to:</Para>
      <GrievanceBlock title="📩 Contact Us" />
    </div>
  );
}

const TAB_CONTENT = {
  terms:      <TermsContent />,
  refund:     <RefundContent />,
  privacy:    <PrivacyContent />,
  cookies:    <CookiesContent />,
  disclaimer: <DisclaimerContent />,
};

/* ═══════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════ */
export default function TermsAndPolicies() {
  const [active, setActive] = useState("terms");
  const contentRef = useRef(null);

  const handleTab = (id) => {
    setActive(id);
    // Scroll to content on mobile
    setTimeout(() => {
      contentRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const lastUpdated = LAST_UPDATED;

  return (
    <>
      <Header />

      {/* ── HERO ── */}
      <div style={{
        position: "relative",
        height: "clamp(220px, 30vw, 320px)",
        background: "url('https://images.unsplash.com/photo-1450101499163-c8848c66ca85?q=80&w=1400') center/cover no-repeat",
        display: "flex", alignItems: "center", justifyContent: "flex-start",
        overflow: "hidden"
      }}>
        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(135deg, rgba(15,42,74,.92) 0%, rgba(31,78,140,.75) 60%, rgba(0,0,0,.4) 100%)"
        }}/>
        <div style={{ position: "relative", zIndex: 1, padding: "0 clamp(20px,6vw,80px)" }}>
          <div style={{
            fontSize: 11, fontWeight: 800, color: "#F39C12",
            textTransform: "uppercase", letterSpacing: ".8px", marginBottom: 12
          }}>Legal & Compliance</div>
          <h1 style={{
            fontFamily: "inherit", fontSize: "clamp(26px,5vw,48px)",
            fontWeight: 900, color: "#fff", margin: "0 0 12px", lineHeight: 1.2
          }}>Terms & Policies</h1>
          <p style={{ color: "rgba(255,255,255,.75)", fontSize: 15, margin: 0, maxWidth: 480 }}>
            Understanding your rights and our commitments at {COMPANY}
          </p>
        </div>
      </div>

      {/* ── PAGE BODY ── */}
      <div style={{ background: "#F8FAFC", minHeight: "60vh", paddingBottom: 80 }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 16px" }}>

          {/* ── TAB BAR ── */}
          <div style={{
            overflowX: "auto", scrollbarWidth: "none",
            borderBottom: `3px solid #E2E8F0`,
            display: "flex", gap: 0
          }}>
            {TABS.map(tab => {
              const isActive = active === tab.id;
              return (
                <button key={tab.id} onClick={() => handleTab(tab.id)} style={{
                  padding: "18px 20px", border: "none", background: "none",
                  fontSize: 14, fontWeight: isActive ? 800 : 600,
                  color: isActive ? BRAND : "#64748B",
                  borderBottom: `3px solid ${isActive ? BRAND : "transparent"}`,
                  marginBottom: -3, cursor: "pointer", whiteSpace: "nowrap",
                  fontFamily: "inherit", transition: "all .2s",
                  flexShrink: 0
                }}>
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* ── TWO COLUMN LAYOUT ── */}
          <div style={{
            display: "grid",
            gridTemplateColumns: "240px 1fr",
            gap: 32,
            paddingTop: 32,
            alignItems: "start"
          }} className="tp-layout">

            {/* LEFT — Quick nav */}
            <div style={{
              background: "#fff", borderRadius: 16,
              border: "1px solid #E2E8F0",
              padding: "20px",
              position: "sticky", top: 80
            }} className="tp-sidenav">
              <div style={{ fontSize: 11, fontWeight: 800, color: "#94A3B8",
                textTransform: "uppercase", letterSpacing: ".5px", marginBottom: 14 }}>
                All Policies
              </div>
              {TABS.map(tab => (
                <button key={tab.id} onClick={() => handleTab(tab.id)} style={{
                  display: "block", width: "100%", textAlign: "left",
                  padding: "10px 12px", borderRadius: 10, border: "none",
                  background: active === tab.id ? "#EFF6FF" : "none",
                  color: active === tab.id ? BRAND : "#475569",
                  fontWeight: active === tab.id ? 800 : 600,
                  fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                  marginBottom: 4, transition: ".15s",
                  borderLeft: `3px solid ${active === tab.id ? BRAND : "transparent"}`
                }}>
                  {tab.label}
                </button>
              ))}
              <div style={{
                marginTop: 20, paddingTop: 16,
                borderTop: "1px solid #F1F5F9",
                fontSize: 11, color: "#94A3B8", lineHeight: 1.6
              }}>
                Last updated:<br />
                <strong style={{ color: "#64748B" }}>{lastUpdated}</strong>
              </div>
            </div>

            {/* RIGHT — Content */}
            <div ref={contentRef}>
              <div style={{
                background: "#fff", borderRadius: 18,
                border: "1px solid #E2E8F0",
                padding: "clamp(20px,4vw,40px)",
                boxShadow: "0 2px 12px rgba(0,0,0,.04)"
              }}>
                {/* Content header */}
                <div style={{
                  display: "flex", justifyContent: "space-between",
                  alignItems: "flex-start", flexWrap: "wrap", gap: 12,
                  marginBottom: 24, paddingBottom: 20,
                  borderBottom: "1px solid #F1F5F9"
                }}>
                  <div>
                    <h2 style={{
                      fontFamily: "inherit", fontSize: "clamp(18px,3vw,26px)",
                      fontWeight: 900, color: "#0F2A4A", margin: "0 0 4px"
                    }}>
                      {TABS.find(t => t.id === active)?.label}
                    </h2>
                    <p style={{ margin: 0, fontSize: 13, color: "#94A3B8" }}>
                      {COMPANY} · Effective {lastUpdated}
                    </p>
                  </div>
                  <span style={{
                    fontSize: 11, fontWeight: 800, padding: "4px 12px",
                    borderRadius: 999, background: "#EFF6FF", color: BRAND
                  }}>
                    Suvidha Elder Care
                  </span>
                </div>

                {/* Actual content */}
                {TAB_CONTENT[active]}

                {/* Footer note */}
                <div style={{
                  marginTop: 32, padding: "16px 20px",
                  background: "#F8FAFC", borderRadius: 12,
                  border: "1px solid #E2E8F0",
                  fontSize: 13, color: "#64748B", lineHeight: 1.7
                }}>
                  <strong style={{ color: "#1E293B" }}>Questions about this policy?</strong><br />
                  Contact us at <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: BRAND, fontWeight: 700 }}>{SUPPORT_EMAIL}</a>{" "}
                  or visit our <a href="/contact" style={{ color: BRAND, fontWeight: 700 }}>Contact page</a>.
                  These terms are governed by the laws of India.
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .tp-layout { grid-template-columns: 1fr !important; }
          .tp-sidenav { display: none !important; }
        }
        @media (max-width: 480px) {
          .tp-layout { padding-top: 16px !important; gap: 16px !important; }
        }
      `}</style>

      <Footer />
    </>
  );
}